import * as React from 'react'
import { Info } from 'lucide-react'
import type { ConsentOptionsDto, ConsentOrgOption } from '@lib/dto/mcp.dto'
import { lowestAccessLevel, MCP_ACCESS_LEVEL_LABELS, type McpAccessLevel } from '@lib/enums/mcp-access-level.enum'
import { grantCodeSchema } from '@lib/input/mcp.input'
import type { Uuid } from '@lib/validators/params'
import { ROLE_LABELS } from '@/components/team/roles'
import { Badge } from '@/components/ui/badge'
import { SegmentedControl, type SegmentedOption } from '@/components/ui/segmented-control'
import type { IMcpConsentService } from '@/types/McpConsentService.types'
import { ConsentLayout } from './ConsentLayout'
import { GrantCodeField } from './GrantCodeField'
import { useConsentSubmit } from './useConsentSubmit'

type MemberOptions = Extract<ConsentOptionsDto, { as: 'tenant_user' }>

interface MemberGrant {
  tenantId: Uuid
  level: McpAccessLevel
}

const LEVEL_HINTS: Record<McpAccessLevel, string> = {
  test_only: 'Test mode only. No live data.',
  live_read: 'Can read live envelopes and logs, but can’t change anything live.',
  live: 'Can act in live mode, including legally binding sends (Owners), with PixelSign confirming the riskiest actions.',
}

function initialLevels(organizations: readonly ConsentOrgOption[]): Map<Uuid, McpAccessLevel> {
  const levels = new Map<Uuid, McpAccessLevel>()
  for (const org of organizations) {
    const level = lowestAccessLevel(org.offeredLevels)
    if (level !== null) levels.set(org.tenantId, level)
  }
  return levels
}

/** Grants with no live changes, or null when one of them is live. */
function grantsWithoutLive(
  grants: readonly MemberGrant[],
): Array<{ tenantId: Uuid; level: Exclude<McpAccessLevel, 'live'> }> | null {
  const safe: Array<{ tenantId: Uuid; level: Exclude<McpAccessLevel, 'live'> }> = []
  for (const grant of grants) {
    const level = grant.level
    if (level === 'live') return null
    safe.push({ tenantId: grant.tenantId, level })
  }
  return safe
}

interface MemberConsentFormProps {
  requestId: Uuid
  options: MemberOptions
  service: IMcpConsentService
  onSignOut: () => void
  onDone: (redirectUrl: string) => void
}

/** What an organization member lets an AI app reach, organization by organization. */
export function MemberConsentForm({ requestId, options, service, onSignOut, onDone }: MemberConsentFormProps) {
  const { pending, errors, setErrors, approve, deny } = useConsentSubmit(requestId, service, onDone)
  const [levels, setLevels] = React.useState(() => initialLevels(options.organizations))
  const [acknowledged, setAcknowledged] = React.useState(false)
  const [code, setCode] = React.useState('')

  const chosen = [...levels.values()]
  const readsLive = chosen.some((level) => level !== 'test_only')
  const changesLive = chosen.includes('live')

  function submit(): void {
    const grants = [...levels].map(([tenantId, level]): MemberGrant => ({ tenantId, level }))
    const safe = grantsWithoutLive(grants)
    if (safe !== null) {
      void approve({ as: 'tenant_user', grants: safe, acknowledged })
      return
    }
    const next: typeof errors = {}
    if (!acknowledged) next.acknowledged = 'Confirm this to give live access.'
    const codeParsed = grantCodeSchema.safeParse(code)
    if (!codeParsed.success) next.code = 'Enter the 6-digit code from the email.'
    if (!acknowledged || !codeParsed.success) {
      setErrors(next)
      return
    }
    void approve({ as: 'tenant_user', grants, acknowledged: true, code: codeParsed.data })
  }

  return (
    <ConsentLayout
      app={options.app}
      user={options.user}
      pending={pending}
      formError={errors.form}
      canConnect={chosen.length > 0}
      onSignOut={onSignOut}
      onApprove={submit}
      onDeny={() => void deny()}
    >
      <div className="space-y-2">
        <h2 className="text-body font-medium">What it can reach</h2>
        {options.organizations.length === 0 ? (
          <p className="text-caption text-muted-foreground">You aren’t a member of any organization.</p>
        ) : (
          <ul className="divide-y divide-border rounded-lg border border-border">
            {options.organizations.map((org) => (
              <OrganizationRow
                key={org.tenantId}
                org={org}
                level={levels.get(org.tenantId)}
                onChange={(level) =>
                  setLevels((current) => {
                    const next = new Map(current)
                    next.set(org.tenantId, level)
                    return next
                  })
                }
              />
            ))}
          </ul>
        )}
      </div>

      {readsLive ? (
        <p className="flex items-start gap-2 rounded-lg border border-info-border bg-info-subtle px-3 py-2 text-body text-info">
          <Info className="mt-0.5 size-4 shrink-0" />
          <span>
            Live borrower data (names, emails, documents) from these organizations will be sent to {options.app.name} and
            its AI provider.
          </span>
        </p>
      ) : null}

      {changesLive ? (
        <div className="space-y-3">
          <div className="space-y-1">
            <label className="flex items-start gap-2 text-body">
              <input
                type="checkbox"
                className="mt-0.5 size-4 shrink-0 accent-primary"
                checked={acknowledged}
                aria-invalid={errors.acknowledged ? true : undefined}
                onChange={(event) => setAcknowledged(event.target.checked)}
              />
              <span>
                I understand that actions {options.app.name} takes are my own, and that envelopes it sends are legally
                binding.
              </span>
            </label>
            {errors.acknowledged ? <p className="text-caption text-danger">{errors.acknowledged}</p> : null}
          </div>
          <GrantCodeField
            email={options.user.email}
            value={code}
            onChange={setCode}
            onSend={() => service.sendCode(requestId, { as: 'tenant_user' })}
            error={errors.code}
          />
        </div>
      ) : null}
    </ConsentLayout>
  )
}

interface OrganizationRowProps {
  org: ConsentOrgOption
  level: McpAccessLevel | undefined
  onChange: (level: McpAccessLevel) => void
}

/** One organization: who the person is there, and the access level to grant. */
function OrganizationRow({ org, level, onChange }: OrganizationRowProps) {
  const choices: SegmentedOption<McpAccessLevel>[] = org.offeredLevels.map((value) => ({
    value,
    label: MCP_ACCESS_LEVEL_LABELS[value],
    tone: value === 'test_only' ? 'default' : 'warning',
  }))
  return (
    <li className="space-y-2 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium">{org.name}</span>
        <Badge tone={org.role === 'owner' ? 'accent' : 'neutral'}>{ROLE_LABELS[org.role]}</Badge>
        {org.status === 'suspended' ? <Badge tone="warning">Suspended (read-only)</Badge> : null}
      </div>
      {level === undefined ? (
        <p className="text-caption text-muted-foreground">MCP is turned off for this organization.</p>
      ) : (
        <>
          <SegmentedControl
            ariaLabel={`Access to ${org.name}`}
            value={level}
            onChange={onChange}
            options={choices}
            fullWidth
          />
          <p className="text-caption text-muted-foreground">{LEVEL_HINTS[level]}</p>
        </>
      )}
    </li>
  )
}
